import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  IconButton,
  Button,
  Typography,
  CircularProgress,
  Paper,
  TextField,
  InputAdornment,
  Stack,
  Chip,
  alpha,
  Fade,
  Tooltip,
  Pagination,
  Skeleton,
} from "@mui/material";
import {
  Close,
  Delete,
  CloudUpload,
  Search,
  Image,
  ErrorOutline,
} from "@mui/icons-material";
import { fetchMedia, uploadMedia, deleteMedia } from "../../api/superAdmin";

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
}

const ITEMS_PER_PAGE = 24;

const searchableText = (item: any) =>
  [
    item.title,
    item.alt_text,
    item.original_filename,
    item.filename,
    item.key,
    item.url,
    ...(Array.isArray(item.tags) ? item.tags : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const displayTitle = (item: any) =>
  item.title || item.original_filename || item.filename || "Untitled image";

const MediaLibraryModal: React.FC<Props> = ({ open, onClose, onSelect }) => {
  const [media, setMedia] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const loadMedia = async () => {
    setLoading(true);
    try {
      const data = await fetchMedia();
      setMedia(data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load media");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadMedia();
      setSearchQuery("");
      setPage(1);
    }
  }, [open]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const filteredMedia = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return media;
    return media.filter((item) => searchableText(item).includes(q));
  }, [media, searchQuery]);

  const totalPages = Math.ceil(filteredMedia.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const paginatedMedia = filteredMedia.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const newMedia = await uploadMedia(file);
      setMedia((prev) => [newMedia, ...prev]);
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      setDeletingId(id);
      await deleteMedia(id);
      setMedia((prev) => prev.filter((m) => m.id !== id));
    } catch (err: any) {
      setError(err?.response?.data?.error || "Cannot delete image");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          height: "90vh",
          maxHeight: "90vh",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 2,
          borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Image sx={{ fontSize: 28, color: "primary.main" }} />
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Media Library
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredMedia.length} files
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              size="small"
              placeholder="Search title, tags, filename..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 260,
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
              }}
            />

            <Button
              variant="contained"
              component="label"
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <CloudUpload />}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {uploading ? "Uploading..." : "Upload"}
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleUpload(e.target.files[0]);
                    e.target.value = "";
                  }
                }}
              />
            </Button>

            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, display: "flex", flexDirection: "column" }}>
        {error && (
          <Paper
            sx={{
              mb: 2,
              px: 2,
              py: 1.25,
              borderRadius: 2,
              bgcolor: "error.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <ErrorOutline fontSize="small" />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {error}
            </Typography>
            <IconButton size="small" sx={{ color: "white" }} onClick={() => setError(null)}>
              <Close fontSize="small" />
            </IconButton>
          </Paper>
        )}

        {loading ? (
          <Grid container spacing={2}>
            {Array.from({ length: 12 }).map((_, i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rectangular" sx={{ width: "100%", paddingTop: "100%", borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : filteredMedia.length === 0 ? (
          <Box sx={{ flex: 1, minHeight: 400, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <Search sx={{ fontSize: 64, color: (theme) => alpha(theme.palette.primary.main, 0.3) }} />
            <Typography variant="h6" color="text.secondary">
              No media found
            </Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ flex: 1, alignContent: "flex-start" }}>
              {paginatedMedia.map((item) => (
                <Grid item xs={6} sm={4} md={3} key={item.id}>
                  <Paper
                    elevation={0}
                    onClick={() => {
                      onSelect(item.url);
                      onClose();
                    }}
                    sx={{
                      position: "relative",
                      cursor: "pointer",
                      overflow: "hidden",
                      borderRadius: 2,
                      border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                      "&:hover": {
                        boxShadow: (theme) => `0 8px 16px ${alpha(theme.palette.common.black, 0.1)}`,
                        "& .media-overlay": { opacity: 1 },
                        "& .delete-button": { opacity: 1 },
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", width: "100%", paddingTop: "100%", bgcolor: 'action.hover' }}>
                      <Box
                        component="img"
                        src={item.url}
                        alt={item.alt_text || displayTitle(item)}
                        loading="lazy"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />

                      <Box
                        className="media-overlay"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(0,0,0,0.35)",
                          opacity: 0,
                          transition: "opacity 0.15s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Chip label="Select" size="small" sx={{ color: "white", bgcolor: "rgba(255,255,255,0.2)" }} />
                      </Box>

                      <Tooltip title="Delete">
                        <IconButton
                          className="delete-button"
                          size="small"
                          onClick={(e) => handleDelete(item.id, e)}
                          disabled={deletingId === item.id}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(255,255,255,0.9)",
                            opacity: { xs: 1, md: 0 },
                            "&:hover": { bgcolor: "error.main", color: "white" },
                          }}
                        >
                          {deletingId === item.id ? <CircularProgress size={16} color="error" /> : <Delete fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <Box sx={{ p: 1.25 }}>
                      <Typography variant="caption" fontWeight={600} noWrap display="block">
                        {displayTitle(item)}
                      </Typography>
                      {Array.isArray(item.tags) && item.tags.length > 0 && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {item.tags.join(", ")}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination count={totalPages} page={page} onChange={(_, newPage) => setPage(newPage)} color="primary" />
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibraryModal;